import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Minus, Plus, ShoppingCart, Package } from 'lucide-react';
import { Dialog, IconButton, Button, LoadingState, EmptyState } from '@printtat/ui';
import { formatPrice } from '@printtat/shared';
import type { CatalogProductListItem, ProductVariantResponse } from '@printtat/shared';
import { productsApi } from '@/services/productsApi';
import { useCartStore } from '@/stores/cartStore';
import { cn } from '@/lib/utils';
import { queryKeys } from '@/lib/queryKeys';

const SIZE_LABELS: Record<string, string> = {
  S: 'Small (S)',
  M: 'Medium (M)',
  L: 'Large (L)',
  XL: 'X-Large (XL)',
  XXL: 'XX-Large (XXL)',
};

interface QuickAddModalProps {
  catalogProduct: CatalogProductListItem;
  onClose: () => void;
}

export const QuickAddModal = ({ catalogProduct, onClose }: QuickAddModalProps) => {
  const addCatalogItem = useCartStore((s) => s.addCatalogItem);

  const { data: product, isLoading } = useQuery({
    queryKey: queryKeys.products.detail(catalogProduct.productId),
    queryFn: () => productsApi.getProduct(catalogProduct.productId),
    staleTime: 60_000,
  });

  const variants = useMemo(() => (product?.variants ?? []).filter((v) => v.isActive), [product?.variants]);

  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [adding, setAdding] = useState(false);

  useEffect(() => { setQuantities({}); }, [catalogProduct.id]);

  const sizes = useMemo(() => {
    const seen = new Set<string>();
    return variants.filter((v) => {
      if (seen.has(v.size)) return false;
      seen.add(v.size);
      return true;
    }).map((v) => v.size);
  }, [variants]);

  const colors = useMemo(() => {
    const seen = new Map<string, { name: string; hex: string }>();
    for (const v of variants) {
      if (!seen.has(v.color) && v.stockCount > 0) {
        seen.set(v.color, { name: v.color, hex: v.colorHex });
      }
    }
    return [...seen.values()];
  }, [variants]);

  const variantMap = useMemo(() => {
    const map = new Map<string, ProductVariantResponse>();
    for (const v of variants) map.set(`${v.color}:${v.size}`, v);
    return map;
  }, [variants]);

  const setQty = (variantId: string, qty: number) => {
    setQuantities((prev) => {
      if (qty <= 0) {
        const next = { ...prev };
        delete next[variantId];
        return next;
      }
      return { ...prev, [variantId]: qty };
    });
  };

  const selections = Object.entries(quantities)
    .filter(([, qty]) => qty > 0)
    .map(([variantId, quantity]) => ({ variantId, quantity }));

  const totalItems = selections.reduce((sum, s) => sum + s.quantity, 0);
  const totalPrice = selections.reduce((sum, s) => {
    const variant = variants.find((v) => v.id === s.variantId);
    return sum + (catalogProduct.price + (variant?.additionalPrice ?? 0)) * s.quantity;
  }, 0);

  const handleAdd = async () => {
    if (selections.length === 0) return;
    setAdding(true);
    try {
      for (const sel of selections) {
        await addCatalogItem(catalogProduct.id, sel.variantId, sel.quantity);
      }
      onClose();
    } catch {
      // error handled by store
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-2xl translate-x-[-50%] translate-y-[-50%] border bg-white shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg max-h-[85vh] flex flex-col">
          <Dialog.Close asChild>
            <IconButton size="sm" className="absolute top-4 right-4 z-10">
              <X size={18} />
            </IconButton>
          </Dialog.Close>

          <div className="flex flex-1 overflow-hidden">
            {/* Left — Preview */}
            <div className="w-80 flex-shrink-0 bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col rounded-l-lg">
              <div className="flex-1 flex flex-col items-center justify-center p-5">
                {catalogProduct.previewUrl ? (
                  <div className="w-full flex-1 min-h-0 rounded-xl overflow-hidden bg-white">
                    <img
                      src={catalogProduct.previewUrl}
                      alt={catalogProduct.displayName}
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : (
                  <Package size={56} className="text-gray-200" />
                )}
              </div>
              <div className="px-5 pb-5 text-center">
                <Dialog.Title className="text-sm font-semibold text-gray-900 truncate">
                  {catalogProduct.displayName}
                </Dialog.Title>
                <Dialog.Description className="text-xs text-gray-400 mt-0.5">
                  {catalogProduct.productName}
                </Dialog.Description>
                <div className="mt-2">
                  <span className="text-base font-bold text-gray-900">{formatPrice(catalogProduct.price)}</span>
                  <span className="text-[10px] text-gray-400 ml-1">/ unit</span>
                </div>
              </div>
            </div>

            {/* Right — Variant selection */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="px-5 pt-5 pb-2">
                <h3 className="text-base font-semibold text-gray-900">Select Variants</h3>
                <p className="text-xs text-gray-400 mt-0.5">Choose sizes and quantities</p>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-3">
                {isLoading ? (
                  <LoadingState message="Loading variants..." />
                ) : colors.length === 0 ? (
                  <EmptyState
                    icon={Package}
                    title="No variants in stock"
                    description="This product currently has no available sizes or colors"
                    className="py-10"
                  />
                ) : (
                  <div className="space-y-5">
                    {colors.map((color) => (
                      <div key={color.name}>
                        <div className="flex items-center gap-2.5 mb-2 pb-1.5 border-b border-gray-100">
                          <span
                            className="w-5 h-5 rounded-full border-2 border-gray-200 flex-shrink-0"
                            style={{ backgroundColor: color.hex }}
                          />
                          <span className="text-sm font-semibold text-gray-900">{color.name}</span>
                        </div>
                        <div className="space-y-1">
                          {sizes.map((size) => {
                            const variant = variantMap.get(`${color.name}:${size}`);
                            if (!variant) return null;
                            const inStock = variant.isActive && variant.stockCount > 0;
                            const qty = quantities[variant.id] ?? 0;
                            const maxQty = variant.stockCount;
                            const label = SIZE_LABELS[size] ?? size;

                            return (
                              <div
                                key={size}
                                className={cn(
                                  'flex items-center justify-between py-1 px-3 rounded-lg transition-colors',
                                  qty > 0 && 'bg-gray-50',
                                )}
                              >
                                <div className={cn('flex items-center gap-2', !inStock && 'opacity-50')}>
                                  <span className={cn(
                                    'text-xs',
                                    !inStock ? 'text-gray-400 line-through' :
                                    qty > 0 ? 'text-gray-900 font-medium' : 'text-gray-500',
                                  )}>
                                    {label}
                                  </span>
                                  {inStock && maxQty <= 5 && (
                                    <span className="text-[10px] text-amber-500">{maxQty} left</span>
                                  )}
                                </div>

                                {inStock ? (
                                  <div className="flex items-center gap-2">
                                    <IconButton size="xs" variant="outline" onClick={() => setQty(variant.id, qty - 1)} disabled={qty <= 0} className={cn(qty === 0 && 'opacity-30')}>
                                      <Minus size={12} />
                                    </IconButton>
                                    <span className={cn('w-5 text-center text-xs font-semibold leading-none', qty > 0 ? 'text-gray-900' : 'text-gray-300')}>
                                      {qty}
                                    </span>
                                    <IconButton size="xs" variant="outline" onClick={() => setQty(variant.id, qty + 1)} disabled={qty >= maxQty} className="!opacity-100 hover:!bg-gray-200 hover:!text-gray-900">
                                      <Plus size={12} />
                                    </IconButton>
                                  </div>
                                ) : (
                                  <span className="text-[10px] text-gray-300">Unavailable</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {!isLoading && colors.length > 0 && (
                <div className="border-t border-gray-200 px-5 py-4 flex items-center justify-between">
                  <div>
                    {totalItems > 0 ? (
                      <>
                        <p className="text-xs text-gray-400">
                          {totalItems} item{totalItems !== 1 ? 's' : ''}
                        </p>
                        <p className="text-lg font-bold text-gray-900">{formatPrice(totalPrice)}</p>
                      </>
                    ) : (
                      <p className="text-xs text-gray-400">Select sizes to add</p>
                    )}
                  </div>
                  <Button loading={adding} onClick={handleAdd} disabled={totalItems === 0}>
                    <ShoppingCart size={14} />
                    Add to Cart
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
